import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import {
  Eye, MousePointerClick, MessageCircle, Users, LogOut, RefreshCw, Lock, TrendingUp, Inbox,
  Delete, IndianRupee, BadgeCheck, Download,
} from 'lucide-react';
import { LotusOm } from '../components/omvedha/Ornaments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASS_KEY = 'omvedha_admin_pass';
const PIE_COLORS = ['#D4AF37', '#5C1616', '#B38B22', '#8A5A12', '#E8A54B'];

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-gold/25 bg-white p-6 shadow-[0_20px_50px_-40px_rgba(92,22,22,0.6)]">
    <div className="flex items-center justify-between">
      <span className="font-sans text-xs uppercase tracking-[0.14em] text-ink/50">{label}</span>
      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 text-gold-deep">
        <Icon className="h-4 w-4" strokeWidth={1.8} />
      </span>
    </div>
    <p className="mt-3 font-serif text-4xl text-ink">{value}</p>
  </div>
);

const Login = ({ onSuccess }) => {
  const [pin, setPin] = useState('');
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const tryLogin = async (code) => {
    if (!code) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/admin/login`, { passcode: code });
      localStorage.setItem(PASS_KEY, code);
      onSuccess(code);
    } catch (err) {
      setError('Incorrect PIN. Please try again.');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const press = (d) => {
    if (loading) return;
    const next = (pin + d).slice(0, 4);
    setPin(next);
    if (next.length === 4) tryLogin(next);
  };

  const submit = (e) => {
    e.preventDefault();
    tryLogin(passcode || pin);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(180deg,#FDFBF7,#F7EFD9)] px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-xs rounded-3xl border border-gold/25 bg-white p-8 text-center shadow-[0_30px_80px_-40px_rgba(92,22,22,0.6)]"
      >
        <LotusOm size={52} className="mx-auto" />
        <h1 className="mt-4 font-serif text-2xl text-ink">Omvedha</h1>
        <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.25em] text-gold-deep">Owner Dashboard</p>

        {/* PIN dots */}
        <div className="mt-6 flex justify-center gap-3">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={`h-3.5 w-3.5 rounded-full border transition-colors ${
                i < pin.length ? 'border-gold bg-gold' : 'border-gold/40'
              }`}
            />
          ))}
        </div>
        {error && <p data-testid="admin-login-error" className="mt-3 font-sans text-sm text-red-600">{error}</p>}

        {/* Keypad */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`pin-${n}`}
              onClick={() => press(String(n))}
              className="rounded-2xl border border-gold/30 bg-ivory py-4 font-serif text-2xl text-ink transition-colors hover:bg-gold/10 active:bg-gold/20"
            >
              {n}
            </button>
          ))}
          <span />
          <button
            type="button"
            data-testid="pin-0"
            onClick={() => press('0')}
            className="rounded-2xl border border-gold/30 bg-ivory py-4 font-serif text-2xl text-ink transition-colors hover:bg-gold/10 active:bg-gold/20"
          >
            0
          </button>
          <button
            type="button"
            data-testid="pin-back"
            onClick={() => setPin((p) => p.slice(0, -1))}
            className="flex items-center justify-center rounded-2xl py-4 text-ink/50 transition-colors hover:text-ink"
          >
            <Delete className="h-6 w-6" />
          </button>
        </div>

        {/* Passcode fallback */}
        <div className="relative mt-6">
          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input
            data-testid="admin-passcode-input"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="or enter passcode"
            className="w-full rounded-full border border-gold/30 bg-ivory py-2.5 pl-10 pr-4 font-sans text-sm text-ink outline-none focus:border-gold"
          />
        </div>
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="mt-4 w-full rounded-full py-3 font-sans text-sm font-semibold uppercase tracking-[0.15em] text-ink disabled:opacity-60"
          style={{ background: 'linear-gradient(180deg,#F5D97A,#D4AF37 45%,#B38B22)' }}
        >
          {loading ? 'Verifying…' : 'Enter Dashboard'}
        </button>
      </form>
    </div>
  );
};

const Admin = () => {
  const [passcode, setPasscode] = useState(localStorage.getItem(PASS_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [chartsReady, setChartsReady] = useState(false);
  const [leadFilter, setLeadFilter] = useState('all'); // all | paid | pending

  useEffect(() => {
    if (data) {
      setChartsReady(false);
      const t = setTimeout(() => {
        setChartsReady(true);
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
      }, 150);
      return () => clearTimeout(t);
    }
  }, [data]);

  const fetchData = useCallback(async (pass) => {
    setLoading(true);
    setErr('');
    try {
      const [res, leadRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers: { 'x-admin-passcode': pass } }),
        axios.get(`${API}/admin/leads`, { headers: { 'x-admin-passcode': pass } }),
      ]);
      setData(res.data);
      setLeads(leadRes.data.leads || []);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(PASS_KEY);
        setAuthed(false);
        setPasscode('');
      } else {
        setErr('Failed to load analytics.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (passcode) fetchData(passcode);
  }, [passcode, fetchData]);

  const logout = () => {
    localStorage.removeItem(PASS_KEY);
    setPasscode('');
    setAuthed(false);
    setData(null);
  };

  if (!authed || !data) {
    return <Login onSuccess={(p) => { setPasscode(p); fetchData(p); }} />;
  }

  const s = data.summary;

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#FDFBF7,#F7EFD9)] pb-20" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-10 border-b border-gold/25 bg-ivory/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
          <div className="flex items-center gap-3">
            <LotusOm size={36} />
            <div>
              <p className="font-serif text-lg text-gold-deep">Omvedha</p>
              <p className="-mt-1 font-sans text-[10px] uppercase tracking-[0.2em] text-ink/50">Analytics Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="digest-btn"
              onClick={async () => {
                try {
                  await axios.post(`${API}/admin/send-digest`, {}, { headers: { 'x-admin-passcode': passcode } });
                  setErr('');
                  alert('Digest email sent to the owner inbox.');
                } catch (e) { setErr('Could not send digest.'); }
              }}
              className="hidden items-center gap-2 rounded-full border border-gold/40 px-4 py-2 font-sans text-xs font-medium text-ink/70 transition-colors hover:bg-gold/10 sm:flex"
            >
              <Inbox className="h-3.5 w-3.5" /> Email Digest
            </button>
            <button
              data-testid="refresh-btn"
              onClick={() => fetchData(passcode)}
              className="flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 font-sans text-xs font-medium text-ink/70 transition-colors hover:bg-gold/10"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-maroon px-4 py-2 font-sans text-xs font-medium text-ivory transition-colors hover:bg-maroon/90"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 pt-8 md:px-8">
        {err && <p className="mb-4 font-sans text-sm text-red-600">{err}</p>}

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard testid="stat-views" icon={Eye} label="Page Views" value={s.total_views} />
          <StatCard testid="stat-sessions" icon={Users} label="Unique Visitors" value={s.unique_sessions} />
          <StatCard testid="stat-book" icon={MousePointerClick} label="Book Clicks" value={s.book_clicks} />
          <StatCard testid="stat-whatsapp" icon={MessageCircle} label="WhatsApp Clicks" value={s.whatsapp_clicks} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard testid="stat-leads" icon={Inbox} label="Seva Leads" value={s.leads ?? leads.length} />
          <StatCard testid="stat-clicks" icon={MousePointerClick} label="Total CTA Clicks" value={s.total_clicks} />
          <StatCard testid="stat-ctr" icon={TrendingUp} label="Click-through Rate" value={`${s.ctr}%`} />
          <StatCard testid="stat-events" icon={Eye} label="Total Events" value={s.total_events} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StatCard testid="stat-collected" icon={IndianRupee} label="Total Collected (Paid)" value={`₹${(s.collected ?? 0).toLocaleString('en-IN')}`} />
          <StatCard testid="stat-payments" icon={BadgeCheck} label="Confirmed Payments" value={s.payments ?? 0} />
        </div>

        {/* Timeseries */}
        <div className="mt-8 rounded-2xl border border-gold/25 bg-white p-6 shadow-[0_20px_50px_-40px_rgba(92,22,22,0.6)]">
          <h2 className="font-serif text-xl text-ink">Traffic — Last 14 Days</h2>
          <div className="mt-6 h-72" data-testid="traffic-chart">
            {chartsReady && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.timeseries} margin={{ left: -20, right: 10 }}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#5C1616" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#5C1616" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5D9B8" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#8A7A55' }} />
                <YAxis tick={{ fontSize: 11, fill: '#8A7A55' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #D4AF37', fontSize: 12 }} />
                <Area type="monotone" dataKey="views" stroke="#B38B22" fill="url(#gv)" strokeWidth={2} name="Views" />
                <Area type="monotone" dataKey="clicks" stroke="#5C1616" fill="url(#gc)" strokeWidth={2} name="Clicks" />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Sources */}
          <div className="rounded-2xl border border-gold/25 bg-white p-6 shadow-[0_20px_50px_-40px_rgba(92,22,22,0.6)]">
            <h2 className="font-serif text-xl text-ink">Traffic Sources</h2>
            <div className="mt-4 h-64" data-testid="sources-chart">
              {chartsReady && data.sources.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.sources} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={90} label={(e) => e.source}>
                      {data.sources.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #D4AF37', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center font-sans text-sm text-ink/40">No data yet</p>
              )}
            </div>
          </div>

          {/* CTA breakdown */}
          <div className="rounded-2xl border border-gold/25 bg-white p-6 shadow-[0_20px_50px_-40px_rgba(92,22,22,0.6)]">
            <h2 className="font-serif text-xl text-ink">CTA Clicks by Location</h2>
            <div className="mt-4 h-64" data-testid="cta-chart">
              {chartsReady && data.cta_labels.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.cta_labels} margin={{ left: -20, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5D9B8" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#8A7A55' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#8A7A55' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #D4AF37', fontSize: 12 }} />
                    <Bar dataKey="count" fill="#D4AF37" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center font-sans text-sm text-ink/40">No clicks yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Seva Leads */}
        <div className="mt-8 rounded-2xl border border-gold/25 bg-white p-6 shadow-[0_20px_50px_-40px_rgba(92,22,22,0.6)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif text-xl text-ink">Seva Leads</h2>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'all', label: `All (${leads.length})` },
                { key: 'paid', label: `Paid (${leads.filter((l) => l.paid).length})` },
                { key: 'pending', label: `Pending (${leads.filter((l) => !l.paid).length})` },
              ].map((f) => (
                <button
                  key={f.key}
                  data-testid={`lead-filter-${f.key}`}
                  onClick={() => setLeadFilter(f.key)}
                  className={`rounded-full px-3 py-1.5 font-sans text-xs font-medium transition-colors ${
                    leadFilter === f.key
                      ? 'bg-maroon text-ivory'
                      : 'border border-gold/40 text-ink/70 hover:bg-gold/10'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <button
                data-testid="export-csv-btn"
                onClick={() => {
                  const rows = leads.filter((l) => (
                    leadFilter === 'all' ? true : leadFilter === 'paid' ? !!l.paid : !l.paid
                  ));
                  const headers = ['Name','Phone','Seva','Gotra','Amount','Paid','Paid Amount','Payment ID','Source','Created At'];
                  const esc = (v) => {
                    const s = v == null ? '' : String(v);
                    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                  };
                  const csv = [headers.join(',')].concat(
                    rows.map((l) => [
                      l.name, l.phone, l.seva, l.gotra || '',
                      l.amount || '', l.paid ? 'Yes' : 'No',
                      l.paid_amount || '', l.payment_id || '',
                      l.source || '', l.created_at || '',
                    ].map(esc).join(','))
                  ).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `omvedha-leads-${leadFilter}-${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1.5 font-sans text-xs font-medium text-ink/70 transition-colors hover:bg-gold/10"
              >
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left" data-testid="leads-table">
              <thead>
                <tr className="border-b border-gold/20 font-sans text-[11px] uppercase tracking-[0.12em] text-ink/50">
                  <th className="pb-3 pr-4">Name</th>
                  <th className="pb-3 pr-4">Phone</th>
                  <th className="pb-3 pr-4">Seva</th>
                  <th className="pb-3 pr-4">Gotra</th>
                  <th className="pb-3 pr-4">Date</th>
                  <th className="pb-3 pr-4">Amount</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 pr-4">Source</th>
                  <th className="pb-3">Received</th>
                </tr>
              </thead>
              <tbody className="font-sans text-sm">
                {leads
                  .filter((l) => (leadFilter === 'all' ? true : leadFilter === 'paid' ? !!l.paid : !l.paid))
                  .map((l, i) => (
                  <tr key={i} className="border-b border-gold/10 text-ink/75" data-testid={`lead-row-${i}`}>
                    <td className="py-2.5 pr-4 font-medium text-ink">{l.name}</td>
                    <td className="py-2.5 pr-4">{l.phone}</td>
                    <td className="py-2.5 pr-4">{l.seva}</td>
                    <td className="py-2.5 pr-4">{l.gotra || '—'}</td>
                    <td className="py-2.5 pr-4">{l.preferred_date || '—'}</td>
                    <td className="py-2.5 pr-4">{l.amount ? `₹${l.amount}` : '—'}</td>
                    <td className="py-2.5 pr-4">
                      {l.paid ? (
                        <span data-testid={`lead-paid-${i}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                          <BadgeCheck className="h-3 w-3" /> Paid{l.paid_amount ? ` ₹${l.paid_amount}` : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">Pending</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 capitalize">{l.source || '—'}</td>
                    <td className="py-2.5 text-xs text-ink/50">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {!leads.filter((l) => (leadFilter === 'all' ? true : leadFilter === 'paid' ? !!l.paid : !l.paid)).length && (
                  <tr><td colSpan={9} className="py-6 text-center text-ink/40">No {leadFilter === 'all' ? '' : leadFilter} seva leads yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent events */}
        <div className="mt-8 rounded-2xl border border-gold/25 bg-white p-6 shadow-[0_20px_50px_-40px_rgba(92,22,22,0.6)]">
          <h2 className="font-serif text-xl text-ink">Recent Activity</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[520px] text-left" data-testid="recent-table">
              <thead>
                <tr className="border-b border-gold/20 font-sans text-[11px] uppercase tracking-[0.12em] text-ink/50">
                  <th className="pb-3 pr-4">Event</th>
                  <th className="pb-3 pr-4">Location</th>
                  <th className="pb-3 pr-4">Source</th>
                  <th className="pb-3 pr-4">Session</th>
                  <th className="pb-3">Time</th>
                </tr>
              </thead>
              <tbody className="font-sans text-sm">
                {data.recent.map((r, i) => (
                  <tr key={i} className="border-b border-gold/10 text-ink/75">
                    <td className="py-2.5 pr-4">
                      <span className="rounded-full bg-gold/15 px-2.5 py-1 text-xs font-medium text-gold-deep">
                        {r.type}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4">{r.label || '—'}</td>
                    <td className="py-2.5 pr-4 capitalize">{r.source || '—'}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs text-ink/45">{r.session_id}</td>
                    <td className="py-2.5 text-xs text-ink/50">
                      {r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
                {!data.recent.length && (
                  <tr><td colSpan={5} className="py-6 text-center text-ink/40">No activity recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Admin;
